// @ts-nocheck

// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Loader2, Tag } from 'lucide-react';
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";

interface MediaCredentialsModalProps {
  isOpen: boolean;
  onClose: () => void;
  mediaCredentialsDescription?: string;
  mediaCredentialsFormId?: string;
  eventId?: string;
  eventName?: string;
  notificationEmail?: string;
  mediaCredentialsPrice?: number;
}

interface FormField {
  id: string;
  field_name: string;
  field_type: string;
  field_label: string;
  is_required: boolean;
  field_options?: string[];
  placeholder?: string;
  sort_order?: number;
}

interface FormResponse {
  [key: string]: string;
}

const MediaCredentialsModal: React.FC<MediaCredentialsModalProps> = ({
  isOpen,
  onClose,
  mediaCredentialsDescription,
  mediaCredentialsFormId,
  eventId,
  eventName,
  notificationEmail,
  mediaCredentialsPrice = 0
}) => {
  const navigate = useNavigate();
  const [formFields, setFormFields] = useState<FormField[]>([]);
  const [formData, setFormData] = useState<FormResponse>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(false);
  const [discountedPrice, setDiscountedPrice] = useState(mediaCredentialsPrice);

  useEffect(() => {
    if (isOpen) {
      fetchFormFields();
      setDiscountedPrice(mediaCredentialsPrice);
      setCouponCode('');
      setCouponApplied(false);
    }
  }, [isOpen, mediaCredentialsFormId, mediaCredentialsPrice]);

  const fetchFormFields = async () => {
    if (!mediaCredentialsFormId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('event_registration_forms')
        .select('fields')
        .eq('id', mediaCredentialsFormId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching form:', error);
        setFormFields([]);
      } else if (!data || !data.fields || data.fields.length === 0) {
        console.log('No form fields found');
        setFormFields([]);
      } else {
        const transformedFields = data.fields.map((field: any, index: number) => ({
          id: field.id?.toString() || index.toString(),
          field_name: field.label.toLowerCase().replace(/[^a-z0-9]/g, '_'),
          field_type: field.type,
          field_label: field.label,
          is_required: field.required || false,
          field_options: field.options || [],
          placeholder: field.placeholder || '',
          sort_order: index
        }));
        setFormFields(transformedFields);
      }
    } catch (error) {
      console.error('Error fetching form fields:', error);
      setFormFields([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim() || !eventId) return;

    try {
      const { data: event } = await supabase
        .from('events')
        .select('media_credentials_coupon_code, media_credentials_coupon_discount_type, media_credentials_coupon_discount_value, media_credentials_price')
        .eq('id', eventId)
        .single();

      if (event?.media_credentials_coupon_code && 
          couponCode.toUpperCase() === event.media_credentials_coupon_code.toUpperCase()) {
        setCouponApplied(true);
        
        // Calculate discounted price based on discount type
        const basePrice = event.media_credentials_price || 0;
        const discountType = event.media_credentials_coupon_discount_type || 'percentage';
        const discountValue = event.media_credentials_coupon_discount_value || 100;
        
        let newPrice = basePrice;
        if (discountType === 'percentage') {
          // Percentage discount (100 = free, 50 = half off, etc.)
          newPrice = Math.round(basePrice * (1 - discountValue / 100));
        } else {
          // Fixed amount discount in cents
          newPrice = Math.max(0, basePrice - (discountValue * 100));
        }
        
        setDiscountedPrice(newPrice);
        
        if (newPrice === 0) {
          toast.success('Coupon applied! Media credentials are now free.');
        } else {
          const savedAmount = (basePrice - newPrice) / 100;
          toast.success(`Coupon applied! You save $${savedAmount.toFixed(2)}.`);
        }
      } else {
        toast.error('Invalid coupon code');
      }
    } catch (error) {
      toast.error('Failed to validate coupon');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const requiredFields = formFields.filter(field => field.is_required);
    for (const field of requiredFields) {
      if (!formData[field.field_name]?.trim()) {
        toast.error(`Please fill in ${field.field_label}`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const nameField = Object.keys(formData).find(key => 
        key.includes('name') && !key.includes('organization')
      );
      const emailField = Object.keys(formData).find(key => key.includes('email'));
      const orgField = Object.keys(formData).find(key => 
        key.includes('organization') || key.includes('publication') || key.includes('company')
      );
      
      const requesterName = nameField ? formData[nameField] : '';
      const requesterEmail = emailField ? formData[emailField] : '';
      const requesterOrganization = orgField ? formData[orgField] : '';

      // If there's a fee and no coupon applied, redirect to payment
      if (discountedPrice > 0) {
        const { data, error } = await invokeEdgeFunction('create-media-credentials-checkout', {
          body: {
            eventId,
            amount: discountedPrice,
            requesterEmail,
            requesterName,
            requesterOrganization,
            formData,
            couponCode: couponApplied ? couponCode : undefined
          }
        });

        if (error) throw error;
        
        // Edge function may return success if coupon made it free server-side
        if (data?.success) {
          toast.success(data.message || 'Media credentials request submitted successfully!');
          setFormData({});
          onClose();
          navigate(`/event-media-credentials?event_id=${eventId}&submitted=true`);
          return;
        }
        
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      } else {
        // Free or coupon applied - submit directly
        const { data, error } = await invokeEdgeFunction('create-media-credentials-checkout', {
          body: {
            eventId,
            amount: 0,
            requesterEmail,
            requesterName,
            requesterOrganization,
            formData,
            couponCode: couponApplied ? couponCode : undefined
          }
        });

        if (error) throw error;

        if (data?.success) {
          toast.success(data.message || 'Media credentials request submitted successfully!');
          setFormData({});
          onClose();
          // Navigate to the landing page with success state
          navigate(`/event-media-credentials?event_id=${eventId}&submitted=true`);
        }
      }
    } catch (error: any) {
      console.error('Error submitting media credentials request:', error);
      const message = error?.message || error?.details || 'Error submitting request. Please try again.';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderFormField = (field: FormField) => {
    const commonProps = {
      id: field.field_name,
      name: field.field_name,
      value: formData[field.field_name] || '',
      onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => 
        handleInputChange(field.field_name, e.target.value),
      required: field.is_required,
      placeholder: field.placeholder || '',
      className: "w-full"
    };

    switch (field.field_type) {
      case 'textarea':
        return <Textarea {...commonProps} rows={4} />;
      case 'email':
        return <Input {...commonProps} type="email" />;
      case 'phone':
        return <Input {...commonProps} type="tel" formatPhone />;
      case 'number':
        return <Input {...commonProps} type="number" />;
      case 'dropdown':
        return (
          <select {...commonProps} className="w-full px-3 py-2 border border-input bg-background rounded-md">
            <option value="">Select an option</option>
            {field.field_options?.map((option, index) => (
              <option key={index} value={option}>
                {option}
              </option>
            ))}
          </select>
        );
      default:
        return <Input {...commonProps} type="text" />;
    }
  };

  const priceDisplay = discountedPrice > 0 ? `$${(discountedPrice / 100).toFixed(2)}` : 'Free';

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Media Credentials Request</DialogTitle>
          {mediaCredentialsPrice > 0 && (
            <p className="text-sm text-muted-foreground">
              Fee: <span className={couponApplied ? 'line-through text-muted-foreground' : 'font-semibold text-primary'}>
                ${(mediaCredentialsPrice / 100).toFixed(2)}
              </span>
              {couponApplied && <span className="ml-2 font-semibold text-green-600">Free</span>}
            </p>
          )}
        </DialogHeader>
        
        <div className="mt-4">
          {mediaCredentialsDescription && (
            <div className="mb-6 p-4 bg-muted/30 rounded-lg">
              <div 
                className="prose prose-sm max-w-none dark:prose-invert 
                  [&_p]:mb-3 [&_p]:leading-relaxed
                  [&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-3 [&_ul]:space-y-2
                  [&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-3 [&_ol]:space-y-2
                  [&_li]:mb-1 [&_li]:leading-relaxed
                  [&_h1]:mb-4 [&_h1]:font-bold [&_h1]:text-xl
                  [&_h2]:mb-3 [&_h2]:font-bold [&_h2]:text-lg
                  [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-base
                  [&_strong]:font-bold [&_b]:font-bold
                  [&_em]:italic [&_i]:italic"
                dangerouslySetInnerHTML={{ __html: mediaCredentialsDescription }}
              />
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              Loading form...
            </div>
          ) : formFields.length > 0 ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {formFields.map((field) => (
                <div key={field.id}>
                  <Label 
                    htmlFor={field.field_name}
                    className="block text-sm font-medium mb-1"
                  >
                    {field.field_label}
                    {field.is_required && <span className="text-red-500 ml-1">*</span>}
                  </Label>
                  {renderFormField(field)}
                </div>
              ))}

              {/* Coupon Code Section */}
              {mediaCredentialsPrice > 0 && !couponApplied && (
                <div className="pt-2 border-t">
                  <Label className="block text-sm font-medium mb-2">Have a coupon code?</Label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        type="text"
                        value={couponCode}
                        onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                        placeholder="Enter code"
                        className="pl-9"
                      />
                    </div>
                    <Button type="button" variant="outline" onClick={handleApplyCoupon}>
                      Apply
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="flex gap-3 pt-4">
                <Button 
                  type="submit" 
                  className="flex-1"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : discountedPrice > 0 ? (
                    `Submit & Pay ${priceDisplay}`
                  ) : (
                    'Submit Request'
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={onClose}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              Unable to load form. Please contact the event organizer.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default MediaCredentialsModal;